import React from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Box, IconButton, Rating, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import SalonPrimaryActions from '../../../client/salonDetails/SalonPrimaryActions';
import SalonPublicButton from '../../salonPublicAppearance/SalonPublicButton';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';


const PureStudioIdentity = ({
  data,
  mode = 'public',
  onBack,
  onToggleFavorite,
  onOpenMobileInfo,
  onBook,
  onContact,
  clientChatEnabled,
}) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const preview = mode === 'preview';
  const actionProps = {
    disabled: preview,
    'aria-disabled': String(preview),
  };
  const hasRating = Number.isFinite(Number(data?.averageRating))
    && Number(data?.reviewCount) > 0;
  const mapsHref = data?.latitude != null && data?.longitude != null
    ? `https://www.google.com/maps?q=${data.latitude},${data.longitude}`
    : null;

  if (!data?.name) return null;

  return (
    <SalonPublicStyleScope
      sectionKey="header"
      component="header"
      data-testid="salon-public-hero"
      data-salon-template-block="identity"
      data-variant="studio_editorial"
      data-density="compact"
      sx={{
        width: '100%',
        flexShrink: 0,
        overflow: 'hidden',
        backgroundColor: theme.salon.surface,
        borderBottom: `1px solid ${theme.salon.border}`,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1280,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          py: { xs: 1.5, md: 2 },
          boxSizing: 'border-box',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <SalonPublicButton
            preserveLegacyStyle
            variant="text"
            size="small"
            startIcon={<ChevronLeftIcon />}
            onClick={onBack}
            {...actionProps}
            sx={{
              color: theme.salon.secondary,
              backgroundColor: 'transparent',
              border: `1px solid ${alpha(theme.salon.secondary, 0.18)}`,
              px: 1.5,
              '&:hover': {
                backgroundColor: alpha(theme.salon.secondary, 0.06),
                borderColor: alpha(theme.salon.secondary, 0.3),
              },
            }}
          >
            {t('actions.back')}
          </SalonPublicButton>

          <Stack direction="row" spacing={1}>
            <IconButton
              aria-label={t('landing_salon_details.informations')}
              onClick={onOpenMobileInfo}
              {...actionProps}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                color: theme.salon.secondary,
                border: `1px solid ${alpha(theme.salon.secondary, 0.18)}`,
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label={t('landing_salon_details.favorite')}
              onClick={onToggleFavorite}
              {...actionProps}
              sx={{
                color: data.isFavorite ? theme.salon.primary : theme.salon.secondary,
                border: `1px solid ${alpha(theme.salon.secondary, 0.18)}`,
                backgroundColor: theme.colors.background.primary,
                '&:hover': { backgroundColor: alpha(theme.salon.primary, 0.08) },
              }}
            >
              {data.isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
          </Stack>
        </Stack>

        <Box
          data-testid="pure-studio-editorial-hero"
          data-layout="editorial-split"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.12fr) minmax(320px, 0.88fr)' },
            alignItems: 'center',
            gap: { xs: 2.5, md: 4 },
            pt: { xs: 2.5, md: 2 },
            pb: { xs: 0.5, md: 1 },
          }}
        >
          <Stack alignItems="flex-start" spacing={1.5} sx={{ minWidth: 0 }}>
            <Box sx={{ width: 44, height: 3, borderRadius: 99, bgcolor: theme.salon.primary }} />
            <Typography
              component="h1"
              data-salon-text-role="title"
              sx={{
                color: theme.salon.onBackground,
                fontSize: { xs: '1.85rem', sm: '2.35rem', md: '2.75rem' },
                fontWeight: 800,
                lineHeight: 1.02,
                letterSpacing: '-0.045em',
                overflowWrap: 'anywhere',
              }}
            >
              {data.name}
            </Typography>

            {data.description ? (
              <Typography
                data-salon-text-role="content"
                sx={{
                  maxWidth: 600,
                  color: theme.colors.gray[600],
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  lineHeight: 1.7,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {data.description}
              </Typography>
            ) : null}

            <Stack spacing={1} data-salon-text-role="content">
              {hasRating ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Rating
                    value={Number(data.averageRating)}
                    precision={0.1}
                    readOnly
                    size="small"
                    aria-label={t('landing_salon_details.reviews_count', { count: data.reviewCount })}
                    sx={{ '& .MuiRating-iconFilled': { color: theme.salon.primary } }}
                  />
                  <Typography sx={{ color: theme.salon.secondary, fontWeight: 700 }}>
                    {Number(data.averageRating).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.colors.gray[500] }}>
                    {t('landing_salon_details.reviews_count_short', { count: data.reviewCount })}
                  </Typography>
                </Stack>
              ) : null}

              {data.address ? (
                <Stack
                  component={mapsHref ? 'a' : 'div'}
                  href={!preview ? mapsHref : undefined}
                  target={mapsHref && !preview ? '_blank' : undefined}
                  rel={mapsHref && !preview ? 'noopener noreferrer' : undefined}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{ color: theme.colors.gray[600], textDecoration: 'none' }}
                >
                  <LocationOnOutlinedIcon sx={{ mt: 0.15, fontSize: '1.15rem', color: theme.salon.primary }} />
                  <Typography variant="body2" sx={{ color: 'inherit' }}>{data.address}</Typography>
                </Stack>
              ) : null}

              {data.phone ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: theme.colors.gray[600] }}>
                  <PhoneOutlinedIcon sx={{ fontSize: '1.05rem', color: theme.salon.primary }} />
                  <Typography variant="body2" sx={{ color: 'inherit' }}>{data.phone}</Typography>
                </Stack>
              ) : null}
            </Stack>

            {!preview && typeof onBook === 'function' ? (
              <SalonPrimaryActions
                inline
                lightSurface
                clientChatEnabled={clientChatEnabled}
                onBook={onBook}
                onContact={onContact}
                bookLabel={t('landing_salon_details.book_appointment')}
              />
            ) : null}
          </Stack>

          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 210, md: 250 },
              pr: { xs: 1.25, md: 1.75 },
              pb: { xs: 1.25, md: 1.75 },
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: { xs: '14px 0 0 14px', md: '18px 0 0 18px' },
                borderRadius: { xs: '18px', md: '22px' },
                bgcolor: alpha(theme.salon.primary, 0.16),
              }}
            />
            {data.heroPhoto ? (
              <Box
                component="img"
                src={data.heroPhoto}
                alt={data.name}
                sx={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  height: { xs: 210, md: 250 },
                  objectFit: 'cover',
                  borderRadius: { xs: '18px', md: '22px' },
                  boxShadow: `0 20px 55px ${alpha(theme.salon.secondary, 0.16)}`,
                }}
              />
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  display: 'grid',
                  placeItems: 'center',
                  width: '100%',
                  height: { xs: 210, md: 250 },
                  borderRadius: { xs: '18px', md: '22px' },
                  color: theme.salon.onPrimary,
                  bgcolor: theme.salon.primary,
                  fontSize: '4rem',
                  fontWeight: 800,
                }}
              >
                {String(data.name).trim().charAt(0).toUpperCase()}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </SalonPublicStyleScope>
  );
};

export default PureStudioIdentity;
